/**
 * Los ratios de la cuenta de resultados y la cascada, DENTRO de Pérdidas y ganancias, debajo de los
 * filtros (acuerdo de la reunión del 22-10-2026). Antes vivían en una pantalla aparte, «KPIs
 * financieros», y para ver el margen de un trimestre había que cambiar de pantalla y volver a elegir
 * el periodo.
 *
 * Usa los MISMOS componentes que esa pantalla (ScoreCard, WaterfallChart y el panel lateral de
 * detalle) y el mismo endpoint, con el periodo que ya eligió el usuario arriba: no puede enseñar
 * unos ratios de un periodo y una tabla de otro. Cada pulsación en una tarjeta abre el panel con la
 * fórmula, los umbrales y las cuentas que la componen.
 *
 * ☠️ Los KPIs no admiten filtro por línea de servicio. Si se eligió una, NO se enseñan (con un
 * aviso) en vez de mezclar ratios de toda la empresa con una tabla de una sola línea.
 */
import { useEffect, useState } from 'react'
import { AlertCircle, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { PnlKpisResponse } from '@jr2os/shared'
import { api, ApiError } from '../../lib/api'
import type { RatioKey } from '../../lib/ratio-insight'
import KpiDrilldownDrawer, { type DrilldownGroup, type KpiDetail } from './KpiDrilldownDrawer'
import { ScoreCard } from './kpis/ScoreCard'
import { VerticalAnalysisTable } from './kpis/VerticalAnalysisTable'
import { WaterfallChart } from './kpis/WaterfallChart'
import { cardDrill, pnlCardDefs, type KpiCardDef } from './kpis/kpi-cards'

export type PnlKpiPeriod = 'YEAR' | 'Q1' | 'Q2' | 'Q3' | 'Q4' | 'MONTH'

export default function PnlRatiosAndWaterfall({
  year,
  period,
  month,
  serviceLine,
}: {
  year: number
  period: PnlKpiPeriod
  /** Solo con `period = 'MONTH'`. */
  month: number
  /** Código de la línea de servicio elegida en la pantalla ('' = todas). */
  serviceLine: string
}) {
  const { t } = useTranslation('accounting')
  const [data, setData] = useState<PnlKpisResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [drawerTitle, setDrawerTitle] = useState('')
  const [drawerGroups, setDrawerGroups] = useState<DrilldownGroup[]>([])
  const [drawerDetail, setDrawerDetail] = useState<KpiDetail | null>(null)

  useEffect(() => {
    if (serviceLine) return
    const ctrl = new AbortController()
    setLoading(true)
    setError(null)
    api
      .get<PnlKpisResponse>(
        `/accounting/kpis/pnl?year=${year}&period=${period}${period === 'MONTH' ? `&month=${month}` : ''}`
      )
      .then((res) => {
        if (!ctrl.signal.aborted) setData(res)
      })
      .catch((err: unknown) => {
        if (ctrl.signal.aborted) return
        setError(
          err instanceof ApiError
            ? ((err.body as { error?: string })?.error ?? err.message)
            : err instanceof Error
              ? err.message
              : t('errorLoad')
        )
        setData(null)
      })
      .finally(() => {
        if (!ctrl.signal.aborted) setLoading(false)
      })
    return () => ctrl.abort()
  }, [year, period, month, serviceLine, t])

  if (serviceLine) {
    return (
      <section className="rounded-xl border border-[var(--border)] bg-[var(--bg-input-soft)] px-4 py-3 text-sm t-muted">
        {t('pnlKpis.serviceLineNote')}
      </section>
    )
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-700 dark:text-red-300">
        <AlertCircle size={16} />
        <span>{error}</span>
      </div>
    )
  }

  if (!data) {
    return loading ? (
      <div className="flex items-center gap-2 text-sm t-muted">
        <Loader2 size={16} className="animate-spin" />
        {t('loading')}
      </div>
    ) : null
  }

  const open = (card: KpiCardDef) => {
    const { title, groups, detail } = cardDrill(card, data.pnl.drilldown, t)
    setDrawerTitle(title)
    setDrawerGroups(groups)
    setDrawerDetail(detail)
    setDrawerOpen(true)
  }

  return (
    <section className={['space-y-4', loading ? 'opacity-70 transition-opacity' : ''].join(' ')}>
      <h2 className="text-sm font-semibold t-secondary uppercase tracking-wider">
        {t('ratiosPnl')}
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
        {pnlCardDefs(data.pnl).map((c) => (
          <ScoreCard
            key={c.key}
            ratio={c.ratio}
            ratioKey={c.key as RatioKey}
            format={c.format}
            onClick={() => open(c)}
          />
        ))}
      </div>

      <WaterfallChart steps={data.pnl.waterfall} taxBooked={data.pnl.vars.IMP !== 0} />

      {/* El análisis vertical de «KPIs financieros» (cada concepto como % de los ingresos), plegado:
          lo primero que se mira son los ratios y la cascada. */}
      <details className="rounded-xl border border-[var(--border)] bg-[var(--bg-input-soft)] group">
        <summary className="cursor-pointer select-none px-4 py-3 text-sm font-semibold t-primary">
          {t('vertical.title')}
          <span className="ml-2 text-xs font-normal t-muted">{t('vertical.subtitle')}</span>
        </summary>
        <div className="px-1 pb-1">
          <VerticalAnalysisTable rows={data.pnl.verticalAnalysis} showHeader={false} />
        </div>
      </details>

      <KpiDrilldownDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={drawerTitle}
        detail={drawerDetail}
        groups={drawerGroups}
      />
    </section>
  )
}
